import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AcademicConnectionsView } from './AcademicConnectionsView';
import { OwnerMailHubView } from './OwnerMailHubView';
import { getCommunicationsOverview, getOwnerMailHub } from '../api';
import { AiTicketPanel } from '@/features/super-ai';
import { Owner10HubShell } from '@/features/owner/components/Owner10HubShell';
import { routes } from '@/lib/routes';

interface CommunicationHubProps {
  onClose?: () => void;
  initialTab?: 'hub' | 'connections' | 'mail' | 'ai-tickets';
}

type CommTab = 'hub' | 'connections' | 'mail' | 'ai-tickets';

function tabFromPath(pathname: string): CommTab | null {
  if (pathname.endsWith('/connections')) return 'connections';
  if (pathname.endsWith('/mail')) return 'mail';
  if (pathname.endsWith('/ai-tickets')) return 'ai-tickets';
  if (pathname.replace(/\/$/, '').endsWith('/communications')) return 'hub';
  return null;
}

const TITLES: Record<Exclude<CommTab, 'mail'>, { title: string; subtitle: string }> = {
  hub: {
    title: 'Коммуникации',
    subtitle: 'Академические связи и почта владельца в одном центре',
  },
  connections: {
    title: 'Академические связи',
    subtitle: 'Единая переписка со всеми разрешёнными категориями платформы',
  },
  'ai-tickets': {
    title: 'Обращения к консультанту',
    subtitle: 'Запросы, на которые консультант не нашёл ответ в базе знаний',
  },
};

export const CommunicationHub: React.FC<CommunicationHubProps> = ({
  onClose,
  initialTab = 'hub',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const close = onClose ?? (() => navigate(routes.owner));
  const pathTab = tabFromPath(location.pathname);
  const [activeTab, setActiveTab] = useState<CommTab>(pathTab ?? initialTab);
  const [connectionsUnread, setConnectionsUnread] = useState(0);
  const [mailNeedsReply, setMailNeedsReply] = useState(0);

  useEffect(() => {
    const next = tabFromPath(location.pathname);
    if (next) setActiveTab(next);
  }, [location.pathname]);

  useEffect(() => {
    getCommunicationsOverview('SYSTEM_OWNER')
      .then((ov) => setConnectionsUnread(ov.unread_count))
      .catch(() => undefined);

    getOwnerMailHub()
      .then((hub) => setMailNeedsReply(hub.needs_reply_count))
      .catch(() => undefined);
  }, []);

  const changeTab = (id: CommTab) => {
    setActiveTab(id);
    if (id === 'hub') navigate(`${routes.owner}/communications`);
    else navigate(`${routes.owner}/communications/${id}`);
  };

  /* Own overlay chrome — match template layers (U142 / biz-ops), not nested hub tabs */
  if (activeTab === 'connections') {
    return (
      <AcademicConnectionsView
        roleContext="SYSTEM_OWNER"
        roleTitle="Владелец системы"
        onBack={() => changeTab('hub')}
        onClose={close}
      />
    );
  }

  if (activeTab === 'mail') {
    return (
      <OwnerMailHubView
        onBack={() => changeTab('hub')}
        onClose={close}
      />
    );
  }

  const meta = TITLES[activeTab as Exclude<CommTab, 'mail'>];

  return (
    <Owner10HubShell
      title={meta.title}
      subtitle={meta.subtitle}
      onClose={close}
      tabs={[
        { id: 'hub', label: 'Обзор' },
        {
          id: 'connections',
          label: `Академические связи${connectionsUnread ? ` · ${connectionsUnread}` : ''}`,
        },
        {
          id: 'mail',
          label: `Почта владельца${mailNeedsReply ? ` · ${mailNeedsReply}` : ''}`,
        },
        { id: 'ai-tickets', label: 'Обращения к консультанту' },
      ]}
      activeTab={activeTab}
      onTabChange={(id) => changeTab(id as CommTab)}
      cards={
        activeTab === 'hub'
          ? [
              {
                id: 'connections',
                code: 'АСВ',
                title: 'Академические связи',
                text: 'Единая переписка со всеми разрешёнными категориями платформы.',
                accent: '#e2ad3a',
                cta: 'Открыть связи',
                meta: ['переписка', 'категории'],
                onOpen: () => changeTab('connections'),
              },
              {
                id: 'mail',
                code: 'ПЧТ',
                title: 'Почта владельца',
                text: 'Служебные аккаунты, классификация писем и связь с заказами.',
                accent: '#49ddb7',
                cta: 'Открыть почту',
                meta: ['почта', 'заказы'],
                onOpen: () => changeTab('mail'),
              },
              {
                id: 'ai-tickets',
                code: 'КСЛ',
                title: 'Обращения к консультанту',
                text: 'Очередь запросов без ответа из базы знаний.',
                accent: '#9e80ff',
                cta: 'Открыть очередь',
                meta: ['консультант', 'очередь'],
                onOpen: () => changeTab('ai-tickets'),
              },
            ]
          : undefined
      }
    >
      {activeTab === 'ai-tickets' ? <AiTicketPanel isOwner /> : null}
    </Owner10HubShell>
  );
};
